import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertifyService, ConfirmModalComponent, GenericTableConfig, Header, UserStore } from 'ccc-commons/core';
import { AccountsService } from 'src/app/core/services/accounts.service';
import { LoginAsService } from 'src/app/core/services/login-as.service';
import { UserModalComponent } from 'src/app/shared/components/user-modal/user-modal/user-modal.component';
import { Subject } from 'rxjs';
import { UserLicenseModalComponent } from '../license-modal/license-modal.component';
import { AccountFilterStoreService } from '../../../../core/store/account.filters.store.service';
import { Account } from '../../../../core/models/account.model';
import { Organization } from '../../../../core/models/organization.model';
import { debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { EmailModalComponent } from 'src/app/shared/modules/accounts/email-modal/email-modal/email-modal.component';
import {AccountDTO} from '@ccc-core/dtos/account.dto';

@Component({
  selector: 'ccc-cca-users-list',
  templateUrl: './accounts-list.component.html',
  styleUrls: ['./accounts-list.component.scss'],
})
export class AccountsListComponent implements OnInit, OnDestroy {

  @Input() orgName = '';
  @Input() orgId = '';
  @Input() addAccount = true;

  isLoading = false;
  isSending = false;
  sendingId = '';

  headers: Header[] = [];
  rows = [];
  isEdit = false;
  accounts: Account[];
  actions = [];

  filters: any;

  // account selection
  selectedAccounts = [];
  allSelected = false;

  // table and pagination config
  totalItems = 0;
  currentPage = 1;
  itemsPerPage = [10, 15, 20, 25]; // TODO: ask for this numbers
  limitPerPage = this.itemsPerPage[0];
  limitPagination = 3;

  atLeastOneSelected = false;

  tableConfig: GenericTableConfig;
  showing: number;

  // sort
  sortUpFirtsName = false;
  sortUpLastsName = false;
  sortUpEmail = false;
  sortUpAffiliation = false;
  sortUpLicences = false;
  sortUpRole = false;
  sortUpStatus = false;
  sortFilter;

  accountId = '';

  @ViewChild('userModal')
  userModal: UserModalComponent;

  @ViewChild('modalSave')
  modalSave: ConfirmModalComponent;

  @ViewChild('modalDeleteAccount')
  modalDeleteAccount: ConfirmModalComponent;
  previousPath = '';
  confirmMessage = `Are you sure you want disable current logged user?
  <br><br> You won\'t be able to use the system afterwards `;
  params: any = {};
  destroySubject: Subject<void> = new Subject();

  @ViewChild('licensesModal')
  licensesModal: UserLicenseModalComponent;

  @ViewChild('editEmail')
  editEmail: EmailModalComponent;

  constructor(
    private accountService: AccountsService,
    private route: ActivatedRoute,
    private loginAsService: LoginAsService,
    private alertify: AlertifyService,
    private router: Router,
    private userStore: UserStore,
    private accountFilter: AccountFilterStoreService,
  ) {}

  ngOnInit() {

    const extras = this.router.getCurrentNavigation()?.extras?.state;
    this.previousPath = extras ? extras.previousPath : '/home';

    this.generateHeaders();

    this.accountFilter.nameFilter
      .pipe(
      takeUntil(this.destroySubject),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((searchText: string) => {
        this.params.fullName = searchText;
        return this.accountService.getAccountsWithParam(this.params);
      })).subscribe(response => {
        this.accounts = response.body;
        this.totalItems = response.pagination.total;
        this.showing = response.body.length;
        this.setTableConfig();
      });

    this.accountFilter.emailFilter.pipe(
      takeUntil(this.destroySubject),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((searchText: string) => {
        this.params.email = encodeURIComponent(searchText);
        return this.accountService.getAccountsWithParam(this.params);
      })).subscribe(response => {
        this.accounts = response.body;
        this.totalItems = response.pagination.total;
        this.showing = response.body.length;
        this.setTableConfig();
      });
  }

  generateHeaders() {
    this.generateCheckAllHeader();

    const firstName = new Header('FIRST NAME');
    firstName.sortField = 'firstName';
    this.headers.push(firstName);

    const lastName = new Header('LAST NAME');
    lastName.sortField = 'lastName';
    this.headers.push(lastName);

    const email = new Header('EMAIL');
    email.sortField = 'email';
    this.headers.push(email);

    const status = new Header('STATUS');
    this.headers.push(status);

    this.headers.push(new Header('ROLE'));
    this.headers.push(new Header('LAST LOGIN'));
    this.headers.push(new Header('ACTIONS'));
  }

  /**
   * generateCheckAllHeader
   * create the header to check all the accounts
   */
  private generateCheckAllHeader(): void {
    const check = new Header('check');
    check.content = '<input type="checkbox" class="custom-selectors" id="check-all-checkbox"> ';
    check.functionToEmit = 'checkAll';
    this.headers.unshift(check);
  }

  /**
   * getAccountsWithFilter
   * get the accounts of the organization with the existing filters
   * @param org optional
   */
  public getAccountsWithFilter(org?: Organization): void {
    // TODO: Dirty hack, Input not working for some odd reason
    if (org) {
      this.orgId = org.id;
      this.orgName = org.name;
    }
    this.isLoading = true;
    this.setParams();
    this.selectedAccounts = [];
    this.accountService.getAccountsWithParam(this.params)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(response => {
        this.accounts = response.body;
        this.totalItems = response.pagination.total;
        this.showing = response.body.length;
        this.setTableConfig();
      });
  }

  setParams() {
    // remove properties from filter object
    delete this.params.sort;
    delete this.params.status;
    delete this.params.ownership;
    delete this.params.deletedAt;

    if (this.sortFilter) {
      this.params.sort = this.sortFilter;
    }

    if (this.filters) {

      if (this.filters['showInactive']) {
        this.params.status = 'Inactive';

      }

      if (this.filters['ownership']) {
        this.params.ownership = 'true';
      }

      if (this.filters['deletedAt']) {
        this.params.deletedAt = 'true';
      }

    }
    if (this.orgId) {
      this.params.organizationId = this.orgId;
    }
    this.params.skip = (this.currentPage - 1) * this.limitPerPage;
    this.params.limit = this.limitPerPage;
  }
  setTableConfig() {
    this.tableConfig = new GenericTableConfig(
      this.headers,
      this.rows,
      this.limitPerPage,
      this.totalItems,
      this.currentPage,
      this.limitPagination,
      this.itemsPerPage,
      'People',
      this.showing
    );
    this.tableConfig.resizeWidth = false;
  }

  onFunctionEmitted(functionEmitted) {
    const functionAsArray = functionEmitted.split(',');

    const functionToCall = functionAsArray[0];

    if (functionToCall === 'limitPerPageChanged') {
      const newLimitPerPage = functionAsArray[1];
      this.currentPage = 1;
      this.onItemPerPageSelected(newLimitPerPage);
    }

    if (functionToCall === 'checkAll') {
      for (let i = 0; i < this.accounts.length; i++) {
        this.selectedAccounts[this.accounts[i].id] =
          functionAsArray[1] === 'true';
      }
      this.atLeastOneSelected = functionAsArray[1] === 'true';
    }

    this.setTableConfig();
  }

  onPageChange(event) {
    this.currentPage = event;
    this.getAccountsWithFilter();
  }

  onItemPerPageSelected(item) {
    this.limitPerPage = item;
    this.getAccountsWithFilter();
  }

  onFiltersEmitted(filters) {
    this.currentPage = 1;
    this.filters = filters;
    this.selectedAccounts = [];
    this.getAccountsWithFilter();
  }

  /***
   * sorts the table
   * @param field to sort by
   */
  public onSort(field: string): void {
    this.sortFilter = field;
    this.getAccountsWithFilter();
  }

  loginAs(account: Account) {
    this.sendingId = account.id;
    this.isSending = true;

    this.loginAsService.addLoginAs(account.id).subscribe((resp) => {
      this.isSending = false;
      this.sendingId = '';


      if (resp.status === 204) {
        this.alertify.success('Now you can login as ' + account.firstName + ' ' + account.lastName);
      }
    }, (err) => {
      this.isSending = false;
      this.sendingId = '';
      this.alertify.error(err.error.message);
    });
  }

  public onNewUser() {
    this.isEdit = false;
    this.userModal.open();
  }


  editUser(accountToEdit) {
    this.isEdit = true;
    this.userModal.openToEdit(accountToEdit);
  }

  editUserLicenses(accountId, accountName) {
    this.licensesModal.open(accountId, accountName, this.orgId, this.orgName);
  }

  deleteUser(accountID) {

    this.accountId = accountID;
    this.modalDeleteAccount.onOpen();

  }

  onNewAccount(newAccount) {
    this.accountService.addAccount(newAccount).subscribe(() => {
      this.alertify.success('New account added successfully');
      this.getAccountsWithFilter();
      this.userModal.onClose();
    }, (err) => {
      this.userModal.handleErrors(err);
    });
  }

  /**
   * onEditAccount
   * when the user modal emits the edited account, it
   * saves the changes in the account
   * @param editedAccount
   */
  public onEditAccount(editedAccount: AccountDTO): void {
    this.accountService.updateAccount(editedAccount)
        .pipe(takeUntil(this.destroySubject))
        .subscribe(
            () => {
            this.getAccountsWithFilter();
            this.alertify.success(
              'Account updated Successfully'
            );
            this.userModal.onClose();
            },
                error => {
              this.userModal.handleErrors(error);
            }
    );
  }

  onConfirmDelete(close) {
    if (close) {
      this.accountService.deleteAccount(this.accountId).subscribe((response) => {
        this.alertify.success('Account deleted successfully');
        this.getAccountsWithFilter();
      }, error => {
        this.userModal.handleErrors(error);
      });
    }

    this.accountId = '';
  }

  /**
   * onCheckAccount
   * marks an account as checked or unchecked
   * and reviews if all accounts are checked
   * @param accountId string with the account id
   * @param event the html event
   */
  public onCheckAccount(accountId: string, event: Event): void {
    this.selectedAccounts[accountId] = (event.currentTarget as HTMLInputElement).checked;

    this.atLeastOneSelected = false;

    for (const account of this.accounts) {
      let isSelected = false;
      if (this.selectedAccounts[account.id] === true) {
        isSelected = true;
        this.atLeastOneSelected = true;
      }
    }
    const input = document.getElementById('check-all-checkbox') as HTMLInputElement;
    if (!!input) {
     const checked = Object.keys(this.selectedAccounts).filter(val => {
          return this.selectedAccounts[val];
      });
      if (checked.length === 0) {
        input.checked = false;
        input.indeterminate = false;
      } else if (checked.length === this.accounts.length) {
        input.checked = true;
        input.indeterminate = false;
      } else {
        input.indeterminate = true;
        input.checked = false;
      }
    }
  }

  applyAction(deactivate: boolean) {
    let newStatus = 'Active';

    if (deactivate) {
      newStatus = 'Inactive';
    }

    const accountsToUpdate: Account[] = [];

    for (let i = 0; i < this.accounts.length; i++) {
      if (this.selectedAccounts[this.accounts[i].id] === true) {
        accountsToUpdate.push(this.accounts[i]);
      }
    }

    let loggedUserChanges = false;
    accountsToUpdate.map((acc) => {
      if (acc.id === this.userStore.getUserValue().id) {
        loggedUserChanges = true;
      }
    });
    if (loggedUserChanges) {
      this.modalSave.openAndSubscribe().then(
        () => {
          this.updateAccounts(accountsToUpdate, newStatus);
        });
    } else {
      this.updateAccounts(accountsToUpdate, newStatus);
    }

  }
  updateAccounts(accountsToUpdate, newStatus) {
    const allPromises = [];

    accountsToUpdate.forEach(account => {
      allPromises.push(this.accountService.updateAccountStatus(newStatus, account.id).toPromise());
    });

    Promise.all(allPromises).then(() => {
      this.selectedAccounts = [];
      this.getAccountsWithFilter();
      this.alertify.success('Account updated successfully');
      this.atLeastOneSelected = false;
      this.allSelected = false;

      this.headers.shift();
      this.generateCheckAllHeader();
      this.setTableConfig();

    }).catch((err) => {
      this.alertify.error(err.error.message);
    });
  }

  getRole(account: Account) {
    const org = (account.organizations.find((accountOrg) => accountOrg.id === this.orgId));
    // if `Owned by organization` is checked, accounts displayed have one affiliation
    if (account.organizations.length > 0 && (account.organizations[0].role || (org && org.role))) {
      return org ? org.role.name : account.organizations[0].role.name;
    }
    return '';
  }

  ngOnDestroy() {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  /**
   * It opens a modal where the email can be modified
   * @param userId user for change email
   * @param userEmail current user email
   */
  editEmailModal(userId: string, userEmail: string) {
    this.editEmail.open(userId, userEmail);
  }

  /**
   * It loads the accounts if changes were made
   * otherwise it just closes the modal
   * @param isUpdated if changes were saved
   */
  onUpdateEmail(isUpdated: boolean) {
    if (isUpdated) {
      this.getAccountsWithFilter();
    }
  }

  /**
   * showMakeActive
   * returns true if at least one of the selected accounts is Inactive
   */
  public showMakeActive(): boolean {
    if (this.accounts === undefined) return false;

    return this.accounts.some(account => {
      return (this.selectedAccounts[account.id] && account.status === 'Inactive');
    });
  }

  /**
   * showMakeInactive
   * returns true if at least one of the selected accounts is Active
   */
  public showMakeInactive(): boolean {
    if (this.accounts === undefined) return false;

    return this.accounts.some(account => {
      return (this.selectedAccounts[account.id] && account.status === 'Active');
    });
  }

  /**
   * getLoginAsTooltip
   * returns the tooltip for the login as button
   * @param account the account to login as
   */
  public getLoginAsTooltip(account: Account): string {
    if (account.status === 'Inactive') {
      return `You can’t use "Login as" with inactive accounts.`;
    }
    return `Login as ${account.firstName}`;
  }
}
